import type { AbsenceEntry } from './blocks';

/**
 * The refusals a booking can come back with that are not failures.
 *
 * Each names a reason the server would not write what was asked, in a way the
 * client can act on rather than only display: the draft is still in the
 * fields, nothing has happened, and what to do next differs by reason.
 */
export const ABSENCE_CONFLICT = {
  /** Somebody else holds this employee. */
  LOCKED: 'absence_user_locked',
  /** These days already carry a change request awaiting a decision. */
  CHANGE_PENDING: 'absence_change_request_pending',
  /** The entry has moved since the pane read it: read it again, then resave. */
  ENTRY_STALE: 'absence_entry_stale',
  /** The submission named days outside the entry it was checked against. */
  ENTRY_SCOPE: 'absence_entry_scope',
} as const;

export type AbsenceConflictCode =
  (typeof ABSENCE_CONFLICT)[keyof typeof ABSENCE_CONFLICT];

/**
 * The server's own name for a refusal, or `null` where it did not give one.
 *
 * Read from the body rather than inferred from the status, because several of
 * these share a status and they are not the same thing to a user.
 */
export function absenceConflictCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== 'object') return null;
  const payload = ((data as { payload?: unknown }).payload ?? data) as {
    code?: unknown;
  };
  return typeof payload.code === 'string' && payload.code ? payload.code : null;
}

/**
 * The entry as the server says it now stands, from a refusal that carries one.
 *
 * `null` both where the refusal named no entry and where the entry is gone --
 * approving a withdrawal deletes the absence it was about, so a token for days
 * that no longer exist is routine. Already in the shape the projection uses,
 * because it is the same serializer.
 */
export function refusedEntry(error: unknown): AbsenceEntry | null {
  if (!error || typeof error !== 'object') return null;
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== 'object') return null;
  const payload = ((data as { payload?: unknown }).payload ?? data) as {
    entry?: unknown;
  };
  const entry = payload.entry;
  if (!entry || typeof entry !== 'object') return null;
  const candidate = entry as Partial<AbsenceEntry>;
  return Array.isArray(candidate.memberIds) &&
    typeof candidate.revision === 'string'
    ? (candidate as AbsenceEntry)
    : null;
}

/**
 * Whether this refusal means "what you read has moved".
 *
 * The one refusal that is answered by looking again: the token the pane held
 * describes a shape the days no longer have, so the projection is refetched
 * and the same edit can be resaved against what is there now. Resubmitting
 * without that would be refused all over again with the same token.
 */
export function isStaleAbsenceEntry(error: unknown): boolean {
  return absenceConflictCode(error) === ABSENCE_CONFLICT.ENTRY_STALE;
}
